/**
 * Укладка слоя промысловых районов на карту MapLibre.
 *
 * Отдельным модулем по той же причине, что и рубежи ИЭЗ: карт в кабинете не
 * одна, а вторая копия укладки разъехалась бы с первой при первой же правке.
 * Этот модуль только КЛАДЁТ готовые фичи; откуда они берутся — забота
 * `useDistrictsLayer`.
 */
import type maplibregl from 'maplibre-gl'

import {
  DISTRICT_DASH,
  DISTRICT_LABEL_MIN_ZOOM,
  districtColor,
  districtLabels,
  districtLayerId,
} from './districts'
import { OVERLAY_PREFIX } from './layerOrder'

export const DISTRICT_SOURCE_ID = OVERLAY_PREFIX + 'fd-src'
/** Подписи живут ОТДЕЛЬНЫМ источником точек: ярлык ставит сервер по видимой
 *  части района, и символ на полигоне встал бы в центр всего района — то есть
 *  за экраном у любого, ушедшего краем за кадр. */
export const DISTRICT_LABEL_SOURCE_ID = OVERLAY_PREFIX + 'fd-label-src'

/** Слои снизу вверх: белая подложка, линия, номер. */
export const DISTRICT_LAYER_IDS: string[] = [
  districtLayerId('casing'),
  districtLayerId('line'),
  districtLayerId('label'),
]

export function applyDistrictLayers(
  map: maplibregl.Map,
  data: GeoJSON.FeatureCollection,
  beforeId?: string,
): void {
  const labels = districtLabels(data)

  const existing = map.getSource(DISTRICT_SOURCE_ID) as maplibregl.GeoJSONSource | undefined
  if (existing) {
    existing.setData(data)
    ;(map.getSource(DISTRICT_LABEL_SOURCE_ID) as maplibregl.GeoJSONSource | undefined)
      ?.setData(labels)
    return
  }
  map.addSource(DISTRICT_SOURCE_ID, { type: 'geojson', data })
  map.addSource(DISTRICT_LABEL_SOURCE_ID, { type: 'geojson', data: labels })

  // MapLibre бросает, если ориентира нет: слой мог не пережить пересборку стиля.
  const before = beforeId && map.getLayer(beforeId) ? beforeId : undefined
  const color = districtColor()

  // Белая подложка: граница ложится и на светлую сушу, и на тёмное поле слоя, и
  // без обводки на одном из двух пропадает.
  map.addLayer({
    id: districtLayerId('casing'),
    type: 'line',
    source: DISTRICT_SOURCE_ID,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': '#ffffff', 'line-width': 3.2, 'line-opacity': 0.65 },
  }, before)

  map.addLayer({
    id: districtLayerId('line'),
    type: 'line',
    source: DISTRICT_SOURCE_ID,
    layout: { 'line-cap': 'butt', 'line-join': 'round' },
    paint: { 'line-color': color, 'line-width': 1.6, 'line-dasharray': DISTRICT_DASH },
  }, before)

  map.addLayer({
    id: districtLayerId('label'),
    type: 'symbol',
    source: DISTRICT_LABEL_SOURCE_ID,
    minzoom: DISTRICT_LABEL_MIN_ZOOM,
    layout: {
      'text-field': ['get', '_label'] as unknown as string,
      'text-size': 12,
      'text-font': ['Noto Sans Bold', 'Open Sans Bold'],
      // Номер района нельзя прятать при тесноте: он и есть содержание слоя.
      // Пересечься ему тут не с чем — районов в кадре десяток, не сотни.
      'text-allow-overlap': true,
      'text-padding': 4,
    },
    paint: {
      'text-color': color,
      'text-halo-color': '#ffffff',
      'text-halo-width': 1.6,
    },
  }, before)
}

export function removeDistrictLayers(map: maplibregl.Map): void {
  for (const id of [...DISTRICT_LAYER_IDS].reverse()) {
    if (map.getLayer(id)) map.removeLayer(id)
  }
  for (const id of [DISTRICT_LABEL_SOURCE_ID, DISTRICT_SOURCE_ID]) {
    if (map.getSource(id)) map.removeSource(id)
  }
}
